<template>
  <div class="po-summary">
    <div
      v-if="brand && breadcrumbLink"
      class="pos-brand"
    >
      <fn-link
        secondary
        :to="!isAppview ? breadcrumbLink : null"
        :class="[
          {
            'pos-brand-no-link': isAppview,
          },
        ]"
        :hide-from-crawl="breadcrumbLinkCrawlHidden"
      >
        <span>{{ breadcrumbText }}</span>
      </fn-link>
    </div>

    <fn-headline
      class="pos-title"
      size="h4"
      :type="headlineType"
    >
      {{ brand }} {{ name }} {{ variantName }}
    </fn-headline>

    <div class="pos-rating">
      <fn-rating
        tag="a"
        :stars="averageRating"
        :count="numberOfReviews"
        :product-code="productCode"
        bv-enabled
        product-overview-page
        show-summary-on-hover
      />
    </div>

    <div class="show-m">
      <div
        v-if="sellingPoints"
        class="pos-selling-points"
      >
        <fn-list small-bullets>
          <li
            v-for="(sellingPoint, index) in sellingPoints"
            :key="index"
            v-html="sellingPoint"
          />
        </fn-list>
      </div>

      <div
        v-if="hasBiocideWarning && !biocide?.noticeText"
        class="pos-warning pos-warning--biozide"
      >
        <div v-html="$t('product.warnings.contains_pesticides')" />
      </div>

      <div v-else-if="biocide?.noticeText">
        <fn-list
          icons
          unstyled
        >
          <li>
            <fn-icon
              icon="circle-i-light"
              color="green-dark"
            />
            <span v-html="biocide?.noticeText" />
            <a @click.prevent="$emit('changeTab', 'biocidesBox')">
              {{ $t('product.biocides.noticeLinkText') }}
            </a>
          </li>
        </fn-list>
      </div>

      <div class="pos-anchors">
        <fn-list
          unstyled
          horizontal-narrow
        >
          <li v-if="composition">
            <fn-link
              no-arrow
              bold
              underline
              @click.prevent="$emit('changeTab', 'detailedContentMaterials')"
            >
              {{ $t('product.contentMaterials') }}
            </fn-link>
          </li>
          <li v-if="feedingSuggestion">
            <fn-link
              no-arrow
              bold
              underline
              @click.prevent="$emit('changeTab', 'detailedFeedingSuggestion')"
            >
              {{ $t('product.feedingSuggestion') }}
            </fn-link>
          </li>
          <li>
            <fn-link
              no-arrow
              bold
              underline
              @click.prevent="$emit('changeTab', 'detailedProductDetails')"
            >
              {{ $t('product.detailsLinkText') }}
            </fn-link>
          </li>
          <li v-if="sizesAndMeasurements">
            <fn-link
              no-arrow
              bold
              underline
              @click.prevent="$emit('modalSizesAndMeasurementsOpened')"
            >
              {{ $t('product.sizesAndMeasurements.title') }}
            </fn-link>
          </li>
          <li v-if="materialsAndCareInstructions">
            <fn-link
              no-arrow
              bold
              underline
              @click.prevent="$emit('changeTab', 'detailedMaterialsAndCareInstructions')"
            >
              {{ $t('product.materialsAndCareInstructions') }}
            </fn-link>
          </li>
        </fn-list>
      </div>

      <!-- desktop badges -->
      <fn-product-badges
        show-price-tip
        :badges="badges"
        :discount="discount"
        :payback-points="paybackPoints"
        column
      />

      <fn-adobe-target-mbox id="pos-adobeTargetBox" />
    </div>
  </div>
</template>

<script>
export default defineComponent({
  props: {
    brand: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    headlineType: {
      type: String,
      default: 'h1',
    },
    variantName: {
      type: String,
      default: '',
    },
    averageRating: {
      type: Number,
      default: 0,
    },
    numberOfReviews: {
      type: Number,
      default: 0,
    },
    sellingPoints: {
      type: Array,
      default: () => [],
    },
    feedingSuggestion: {
      type: Boolean,
    },
    composition: {
      type: String,
      default: '',
    },
    badges: {
      type: [Object, Boolean],
      default: () => ({}),
    },
    nodes: {
      type: Array,
      default: () => [],
    },
    productCode: {
      type: [String, Number],
      default: '',
    },
    hasBiocideWarning: {
      type: Boolean,
      required: true,
    },
    biocide: {
      type: Object,
      default: () => ({}),
    },
    breadcrumbLink: {
      type: String,
      default: null,
    },
    breadcrumbLinkCrawlHidden: {
      type: Boolean,
      default: false,
    },
    discount: {
      type: String,
      default: null,
    },
    paybackPoints: {
      type: [Object, String],
      default: () => ({}),
    },
    sizesAndMeasurements: {
      type: Boolean,
    },
    materialsAndCareInstructions: {
      type: Boolean,
    },
  },
  emits: ['changeTab', 'modalSizesAndMeasurementsOpened'],
  computed: {
    ...mapState(useMainStore, {
      isAppview: 'isAppview',
      isMobileView: 'isMobileView',
    }),
    breadcrumbText() {
      let breadcrumbText = this.brand;

      if (!this.nodes || this.nodes.length <= 2) {
        return breadcrumbText;
      }

      if (this.nodes.length === 3 && breadcrumbText !== this.nodes[1].title) {
        breadcrumbText += ` ${this.nodes[1].title || this.nodes[1].facetValueName}`;
      } else if (this.nodes.length === 4) {
        breadcrumbText += ` ${this.nodes[2].title || this.nodes[2].facetValueName}`;
      }

      return breadcrumbText;
    },
  },
});
</script>

<style lang="scss" scoped>
.po-summary {
  .pos-brand {
    .link {
      text-transform: uppercase;
      font-size: map-get($font-sizes, 'm');
      color: palette(content, secondary);

      @include bp(m) {
        font-size: map-get($font-sizes, 'l');
      }
    }

    .pos-brand-no-link {
      cursor: unset;

      &:hover :deep(.link-text) {
        text-decoration: none;
      }
    }
  }

  .pos-title {
    color: palette(grey, dark);
    margin-bottom: math.div($pad, 2);
  }

  .pos-selling-points {
    color: palette(grey, dark);
    margin-bottom: $pad;
  }

  .pos-warning--biozide {
    padding: $pad 0;
  }

  .pos-anchors {
    margin-top: math.div($pad, 2);
    margin-bottom: $space;
  }

  .pos-rating {
    margin-bottom: $pad;
    height: 28px;
    line-height: 28px;

    @include bp(m, max) {
      min-height: 46px;
    }

    @include bp(s, max) {
      margin-bottom: 1.25 * $pad;
    }
  }
}
</style>
